/**
 * Login del POS web. Acepta cualquier rol de tenant (admin/manager/cashier/waiter);
 * superadmin no debería loguearse acá — su entrada es el admin backoffice.
 */

import { Button, Card, CardContent } from '@atreus/ui';
import { LogIn } from 'lucide-react';
import { useState } from 'react';

import {
  saveSession,
  type PosSession,
  type PosUserRole,
} from '../auth/session.js';
import { ApiError, apiPost } from '../lib/cloud.js';

interface LoginResponse {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  tenant: { id: string; name: string };
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
  };
}

const POS_ROLES: ReadonlySet<string> = new Set<PosUserRole>([
  'admin',
  'manager',
  'cashier',
  'waiter',
]);

export const LoginPage = ({ onLogin }: { onLogin: () => void }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      const resp = await apiPost<LoginResponse>('/auth/login', {
        email: email.trim(),
        password,
      });
      if (!POS_ROLES.has(resp.user.role)) {
        setError(
          'Esta cuenta no puede usar el POS. Usa el backoffice de admin.',
        );
        setPending(false);
        return;
      }
      const session: PosSession = {
        accessToken: resp.accessToken,
        accessExpiresAt: resp.accessExpiresAt,
        refreshToken: resp.refreshToken,
        user: {
          id: resp.user.id,
          email: resp.user.email,
          fullName: resp.user.fullName,
          role: resp.user.role as PosUserRole,
        },
        tenantId: resp.tenant.id,
        tenantName: resp.tenant.name,
        savedAt: Date.now(),
      };
      saveSession(session);
      onLogin();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Email o contraseña incorrectos.');
      } else {
        setError((err as Error).message);
      }
      setPending(false);
    }
  };

  const disabled = pending || email.trim() === '' || password === '';

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md p-6">
        <div className="mb-8 flex justify-center">
          <img
            src="/atreus-logo-stacked.svg"
            alt="Atreus"
            className="h-32 w-auto dark:invert"
          />
        </div>

        <Card>
          <CardContent className="pt-6">
            <h1 className="text-center text-2xl font-black tracking-tight">
              Iniciar sesión
            </h1>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label
                  htmlFor="login-email"
                  className="mb-1 block text-sm font-semibold"
                >
                  Email
                </label>
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  // `username` ayuda a Chrome a asociar credenciales
                  // guardadas con el campo de contraseña de abajo.
                  autoComplete="username"
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={pending}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                />
              </div>
              <div>
                <label
                  htmlFor="login-password"
                  className="mb-1 block text-sm font-semibold"
                >
                  Contraseña
                </label>
                <input
                  id="login-password"
                  name="password"
                  type="password"
                  // `current-password` = "el usuario está iniciando
                  // sesión, rellena con la guardada, NO ofrezcas
                  // generador". Con `new-password` Chrome mostraba el
                  // popup "te generé una contraseña segura" en cada
                  // focus — molesto en un login.
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={pending}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
                />
              </div>

              {error ? <p className="text-sm text-danger">{error}</p> : null}

              <Button type="submit" size="lg" className="w-full" disabled={disabled}>
                <LogIn className="size-4" />
                {pending ? 'Entrando…' : 'Entrar'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
